import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isToday } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { habitAPI } from '../lib/supabase' 

const { FiPlus, FiTarget, FiTrendingUp, FiCalendar, FiCheck, FiChevronLeft, FiChevronRight, FiZap, FiAward, FiSun, FiMoon, FiRefreshCw, FiArrowRight, FiStar, FiExternalLink, FiList } = FiIcons 

const Dashboard = () => {
  const navigate = useNavigate()
  const [habits, setHabits] = useState([]) 
  const [progress, setProgress] = useState({}) 
  const [loading, setLoading] = useState(true) 
  const [selectedWeek, setSelectedWeek] = useState(new Date()) 
  const [timeOfDay, setTimeOfDay] = useState('') 
  const [dailyAffirmation, setDailyAffirmation] = useState('') 
  const [affirmationType, setAffirmationType] = useState('morning') 
  const [completingHabits, setCompletingHabits] = useState(new Set()) 
  const [showBizAIPromo, setShowBizAIPromo] = useState(true) 

  useEffect(() => {
    loadHabits() 
    determineTimeOfDay()
  }, []) 

  useEffect(() => {
    if (habits.length > 0) {
      loadProgress()
    }
  }, [habits, selectedWeek]) 

  useEffect(() => {
    generateAffirmation()
  }, [progress, affirmationType, habits]) 

  const determineTimeOfDay = () => {
    const hour = new Date().getHours() 
    const newTimeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening' 
    setTimeOfDay(newTimeOfDay) 
    setAffirmationType(hour < 15 ? 'morning' : 'evening')
  } 

  const loadHabits = async () => {
    try {
      const { data, error } = await habitAPI.getHabits() 
      if (error) throw error 
      setHabits(data || [])
    } catch (error) {
      console.error('Error loading habits:', error) 
    } finally {
      setLoading(false)
    } 
  } 

  const loadProgress = async () => {
    const weekStart = format(startOfWeek(selectedWeek), 'yyyy-MM-dd') 
    const weekEnd = format(endOfWeek(selectedWeek), 'yyyy-MM-dd') 
    const progressData = {} 
    for (const habit of habits) {
      const { data } = await habitAPI.getProgress(habit.id, weekStart, weekEnd) 
      progressData[habit.id] = data || [] 
    } 
    setProgress(progressData)
  } 

  const toggleHabitCompletion = async (habitId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd') 
    const currentProgress = progress[habitId] || [] 
    const existingEntry = currentProgress.find(p => p.date === dateStr) 
    const newCompleted = !existingEntry?.completed 
    // Add to completing set for immediate UI feedback 
    setCompletingHabits(prev => new Set(prev).add(`${habitId}-${dateStr}`)) 
    // Optimistically update the UI immediately 
    setProgress(prev => ({
      ...prev,
      [habitId]: prev[habitId]?.map(p => p.date === dateStr ? { ...p, completed: newCompleted } : p ) || [{ habit_id: habitId, date: dateStr, completed: newCompleted }]
    })) 
    try {
      await habitAPI.markComplete(habitId, dateStr, newCompleted) 
      // Reload habits to get updated streak 
      loadHabits()
    } catch (error) {
      console.error('Error updating progress:', error) 
      // Revert the optimistic update on error 
      setProgress(prev => ({
        ...prev,
        [habitId]: prev[habitId]?.map(p => p.date === dateStr ? { ...p, completed: !newCompleted } : p ) || []
      }))
    } finally {
      // Remove from completing set after a short delay for animation 
      setTimeout(() => {
        setCompletingHabits(prev => {
          const newSet = new Set(prev) 
          newSet.delete(`${habitId}-${dateStr}`) 
          return newSet
        })
      }, 600)
    } 
  } 

  const getWeekDays = () => {
    const start = startOfWeek(selectedWeek) 
    const end = endOfWeek(selectedWeek) 
    return eachDayOfInterval({ start, end }) 
  } 

  const isHabitCompleted = (habitId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd') 
    const habitProgress = progress[habitId] || [] 
    return habitProgress.find(p => p.date === dateStr)?.completed || false 
  } 

  const getWeeklyStats = () => {
    const weekDays = getWeekDays() 
    let totalPossible = habits.length * weekDays.length 
    let totalCompleted = 0 
    habits.forEach(habit => {
      weekDays.forEach(day => {
        if (isHabitCompleted(habit.id, day)) {
          totalCompleted++
        }
      })
    }) 
    return {
      completionRate: totalPossible > 0 ? Math.round((totalCompleted / totalPossible) * 100) : 0,
      totalCompleted,
      totalPossible,
      bestStreak: Math.max(...habits.map(h => h.streak), 0),
      perfectDays: weekDays.filter(day => habits.every(habit => isHabitCompleted(habit.id, day)) ).length
    } 
  } 

  const generateAffirmation = () => {
    const weeklyStats = getWeeklyStats() 
    const today = new Date() 
    const yesterday = new Date(today) 
    yesterday.setDate(yesterday.getDate() - 1) 
    const yesterdayStr = format(yesterday, 'yyyy-MM-dd') 
    let yesterdayCompleted = 0 
    let yesterdayTotal = habits.length 
    habits.forEach(habit => {
      const habitProgress = progress[habit.id] || [] 
      if (habitProgress.find(p => p.date === yesterdayStr && p.completed)) {
        yesterdayCompleted++
      }
    }) 
    const yesterdayRate = yesterdayTotal > 0 ? Math.round((yesterdayCompleted / yesterdayTotal) * 100) : 0 
    const morningAffirmations = [ 
      `Yesterday you dominated ${yesterdayCompleted} out of ${yesterdayTotal} habits. Today, forge even greater power!`,
      `You're ${weeklyStats.completionRate}% consistent this week. Your discipline is becoming unstoppable!`,
      `Your longest streak is ${weeklyStats.bestStreak} days. That's the mark of a true champion!`,
      `You've had ${weeklyStats.perfectDays} perfect days this week. Today can be another victory!`,
      `Every completed habit forges your character. Your transformation continues today!` 
    ] 
    const eveningAffirmations = [ 
      `Tomorrow is another battlefield. You're armed with the discipline to dominate.`,
      `You're ${weeklyStats.completionRate}% of the way to weekly mastery. Victory is within reach!`,
      `Your consistency this week is forging an unbreakable mindset.`,
      `Tomorrow, remember your power. Your future self depends on tonight's commitment.`,
      `Focus on relentless progress. Every habit completed builds your empire.` 
    ] 
    const affirmations = affirmationType === 'morning' ? morningAffirmations : eveningAffirmations 
    if (habits.length === 0) {
      setDailyAffirmation( 
        affirmationType === 'morning' 
          ? 'Today is the perfect day to begin forging your empire. Start small, dominate completely.' 
          : 'Tomorrow is a blank canvas. Forge habits that will build the life you command.' 
      ) 
      return
    } 
    const randomIndex = Math.floor(Math.random() * affirmations.length) 
    setDailyAffirmation(affirmations[randomIndex])
  } 

  const weeklyStats = getWeeklyStats() 

  if (loading) {
    return ( 
      <div className="min-h-screen bg-underworld-gradient flex items-center justify-center"> 
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }} 
          animate={{ opacity: 1, scale: 1 }} 
          className="text-center" 
        > 
          <div className="w-16 h-16 border-4 border-dreamforge-yellow/30 border-t-dreamforge-yellow rounded-full animate-spin mb-4"></div> 
          <h3 className="text-xl font-bold text-dreamforge-yellow mb-2">Loading Command Center</h3> 
          <p className="text-dreamforge-yellow/60">Preparing your forge...</p> 
        </motion.div> 
      </div> 
    ) 
  } 

  return ( 
    <div className="min-h-screen bg-underworld-gradient"> 
      <div className="max-w-6xl mx-auto px-4 py-6"> 
        {/* Header */} 
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="text-center mb-8" 
        > 
          <div className="flex items-center justify-center space-x-2 mb-4"> 
            <SafeIcon icon={FiZap} className="text-2xl text-dreamforge-yellow" /> 
            <h1 className="text-3xl font-bold text-forge"> Command Center </h1> 
          </div> 
          <p className="text-dreamforge-yellow/80 px-4 text-lg"> 
            <span className="text-dreamforge-yellow font-semibold">Forge Your Future, Dominate the Game</span> 
          </p> 
        </motion.div> 

        {/* Biz AI Coach Promo - Mobile Optimized */} 
        {showBizAIPromo && ( 
          <motion.div 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="mb-6 relative" 
          > 
            <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 backdrop-blur-sm rounded-2xl p-3 sm:p-4 border border-purple-400/30 shadow-power"> 
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4"> 
                <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 flex-1"> 
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl overflow-hidden border-2 border-purple-400/50 flex-shrink-0"> 
                    <img 
                      src="https://assets.cdn.filesafe.space/2LqsTomEY3T009LGB5ZG/media/671cfed4b62b363560d3d2a7.png" 
                      alt="Biz AI Coach" 
                      className="w-full h-full object-cover" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiM4YjVjZjYiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGFsaWdubWVudC1iYXNlbGluZT0ibWlkZGxlIiBmaWxsPSJ3aGl0ZSI+QUk8L3RleHQ+PC9zdmc+';
                      }}
                    /> 
                  </div> 
                  <div className="flex-1 min-w-0"> 
                    <h3 className="text-purple-300 font-semibold text-sm sm:text-base leading-tight mb-1"> 
                      🚀 Transform Self-Reflection into Breakthrough Insight 
                    </h3> 
                    <p className="text-purple-400/80 text-xs sm:text-sm leading-tight"> 
                      Guides you through deep reflection, helping you access your inner wisdom and develop self-awareness. 
                    </p> 
                  </div> 
                </div> 
                <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3"> 
                  <a 
                    href="https://bizunderworld.my-ai.coach" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm" 
                  > 
                    <span>Start Coaching</span> 
                    <SafeIcon icon={FiExternalLink} className="text-xs" /> 
                  </a> 
                  <button 
                    onClick={() => setShowBizAIPromo(false)} 
                    className="text-purple-400/60 hover:text-purple-400 transition-colors p-1 text-lg" 
                  > 
                    ✕ 
                  </button> 
                </div> 
              </div> 
            </div> 
          </motion.div> 
        )} 

        {/* Daily Affirmation */} 
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.1 }} 
          className="card-forge rounded-2xl shadow-forge p-6 mb-6 relative overflow-hidden glow-forge" 
        > 
          <div className="absolute top-0 right-0 w-24 h-24 bg-forge-gradient opacity-10 rounded-bl-full"></div> 
          <div className="flex items-center justify-between mb-4"> 
            <div className="flex items-center space-x-2"> 
              <SafeIcon 
                icon={affirmationType === 'morning' ? FiSun : FiMoon} 
                className={`text-xl ${affirmationType === 'morning' ? 'text-dreamforge-yellow' : 'text-dreamforge-blue'}`} 
              /> 
              <h2 className="text-xl font-bold text-dreamforge-yellow"> 
                {affirmationType === 'morning' ? 'Morning Power' : 'Evening Reflection'} 
              </h2> 
            </div> 
            <div className="flex items-center space-x-2"> 
              <button 
                onClick={() => setAffirmationType(affirmationType === 'morning' ? 'evening' : 'morning')} 
                className="btn-secondary p-2 rounded-full shadow hover:shadow-md transition-all duration-300" 
              > 
                <SafeIcon icon={affirmationType === 'morning' ? FiMoon : FiSun} /> 
              </button> 
              <button 
                onClick={generateAffirmation} 
                className="btn-secondary p-2 rounded-full shadow hover:shadow-md transition-all duration-300" 
              > 
                <SafeIcon icon={FiRefreshCw} /> 
              </button> 
            </div> 
          </div> 
          <div className="bg-power-gradient rounded-xl p-4 relative"> 
            <p className="text-lg text-dreamforge-yellow font-medium italic"> 
              "{dailyAffirmation}" 
            </p> 
          </div> 
        </motion.div> 

        {/* Stats Grid */} 
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6"> 
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.2 }} 
            className="card-forge rounded-2xl p-4 shadow-forge hover-forge" 
          > 
            <div className="flex items-center justify-between mb-3"> 
              <div className="p-2 bg-forge-gradient rounded-lg"> 
                <SafeIcon icon={FiTarget} className="text-dark-950" /> 
              </div> 
              <div className="text-right"> 
                <div className="text-2xl font-bold text-dreamforge-yellow">{weeklyStats.completionRate}%</div> 
                <div className="text-xs text-dreamforge-yellow/60">This Week</div> 
              </div> 
            </div> 
            <div className="progress-forge h-2 rounded-full"> 
              <motion.div 
                initial={{ width: 0 }} 
                animate={{ width: `${weeklyStats.completionRate}%` }} 
                transition={{ duration: 1, ease: "easeOut" }} 
                className="progress-forge-fill" 
              /> 
            </div> 
          </motion.div> 

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.3 }} 
            className="card-forge rounded-2xl p-4 shadow-forge hover-forge" 
          > 
            <div className="flex items-center justify-between mb-2"> 
              <div className="p-2 bg-power-gradient rounded-lg"> 
                <SafeIcon icon={FiTrendingUp} className="text-dreamforge-yellow" /> 
              </div> 
              <div className="text-right"> 
                <div className="text-2xl font-bold text-dreamforge-yellow">{habits.length}</div> 
                <div className="text-xs text-dreamforge-yellow/60">Active Habits</div> 
              </div> 
            </div> 
          </motion.div> 

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.4 }} 
            className="card-forge rounded-2xl p-4 shadow-forge hover-forge" 
          > 
            <div className="flex items-center justify-between mb-2"> 
              <div className="p-2 bg-forge-gradient rounded-lg"> 
                <SafeIcon icon={FiZap} className="text-dark-950" /> 
              </div> 
              <div className="text-right"> 
                <div className="text-2xl font-bold text-dreamforge-yellow">{weeklyStats.bestStreak}</div> 
                <div className="text-xs text-dreamforge-yellow/60">Best Streak</div> 
              </div> 
            </div> 
          </motion.div> 

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.5 }} 
            className="card-forge rounded-2xl p-4 shadow-forge hover-forge" 
          > 
            <div className="flex items-center justify-between mb-2"> 
              <div className="p-2 bg-power-gradient rounded-lg"> 
                <SafeIcon icon={FiAward} className="text-dreamforge-yellow" /> 
              </div> 
              <div className="text-right"> 
                <div className="text-2xl font-bold text-dreamforge-yellow">{weeklyStats.perfectDays}</div> 
                <div className="text-xs text-dreamforge-yellow/60">Perfect Days</div> 
              </div> 
            </div> 
          </motion.div> 
        </div> 

        {/* Habits Overview */} 
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.6 }} 
          className="card-forge rounded-2xl shadow-forge overflow-hidden" 
        > 
          <div className="bg-power-gradient p-6 border-b border-dreamforge-yellow/20"> 
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"> 
              <div> 
                <h2 className="text-xl font-bold text-dreamforge-yellow mb-2">Weekly Dominance</h2> 
                <p className="text-dreamforge-yellow/80 text-sm">Track your daily victories</p> 
              </div> 
              <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-2"> 
                {/* Mobile: Add Habit Button */}
                <button 
                  onClick={() => navigate('/habits')} 
                  className="bg-forge-gradient text-dark-950 px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center space-x-1 sm:space-x-2 text-sm" 
                > 
                  <SafeIcon icon={FiPlus} className="text-sm" /> 
                  <span className="hidden sm:inline">Create Habit</span>
                  <span className="sm:hidden">Add</span>
                </button>
                
                {/* Week Navigation */}
                <div className="flex items-center space-x-2"> 
                  <button 
                    onClick={() => setSelectedWeek(new Date(selectedWeek.getTime() - 7 * 24 * 60 * 60 * 1000))} 
                    className="btn-secondary p-2 rounded-lg shadow hover:shadow-md transition-all duration-300" 
                  > 
                    <SafeIcon icon={FiChevronLeft} /> 
                  </button> 
                  <div className="text-center px-2 sm:px-4 py-2 bg-dreamforge-black/50 rounded-lg shadow"> 
                    <div className="text-xs text-dreamforge-yellow/60">Week of</div> 
                    <div className="font-semibold text-dreamforge-yellow text-xs sm:text-sm"> 
                      {format(startOfWeek(selectedWeek), 'MMM d')} - {format(endOfWeek(selectedWeek), 'MMM d, yyyy')} 
                    </div> 
                  </div> 
                  <button 
                    onClick={() => setSelectedWeek(new Date(selectedWeek.getTime() + 7 * 24 * 60 * 60 * 1000))} 
                    className="btn-secondary p-2 rounded-lg shadow hover:shadow-md transition-all duration-300" 
                  > 
                    <SafeIcon icon={FiChevronRight} /> 
                  </button> 
                </div> 
              </div> 
            </div> 
          </div> 
        </motion.div> 

        {/* Mobile-optimized habit tracking */} 
        <div className="p-4"> 
          {habits.length === 0 ? ( 
            <div className="text-center py-12"> 
              <div className="w-16 h-16 bg-forge-gradient rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-forge"> 
                <SafeIcon icon={FiTarget} className="text-dark-950 text-xl" /> 
              </div> 
              <h3 className="text-lg font-bold text-dreamforge-yellow mb-3">Begin Your Forge</h3> 
              <p className="text-dreamforge-yellow/60 mb-6 px-4"> 
                Create your first habit to start building unstoppable momentum. 
              </p> 
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button 
                  onClick={() => navigate('/habits')}
                  className="btn-forge px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                > 
                  <div className="flex items-center justify-center space-x-2"> 
                    <SafeIcon icon={FiPlus} /> 
                    <span>Forge First Habit</span> 
                  </div> 
                </button>
                <button 
                  onClick={() => navigate('/habits')}
                  className="btn-secondary px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                > 
                  <div className="flex items-center justify-center space-x-2"> 
                    <SafeIcon icon={FiList} /> 
                    <span>View All Habits</span> 
                  </div> 
                </button>
              </div>
            </div> 
          ) : ( 
            <div className="space-y-4"> 
              {/* Week days header */} 
              <div className="grid grid-cols-8 gap-2 mb-4"> 
                <div className="text-xs font-medium text-dreamforge-yellow/60 p-2">Habit</div> 
                {getWeekDays().map(day => ( 
                  <div key={day.toISOString()} className="text-center"> 
                    <div className={`text-xs font-medium ${isToday(day) ? 'text-dreamforge-yellow' : 'text-dreamforge-yellow/60'}`}> 
                      {format(day, 'EEE')} 
                    </div> 
                    <div className={`text-sm font-bold ${isToday(day) ? 'text-dreamforge-yellow' : 'text-dreamforge-yellow/80'}`}> 
                      {format(day, 'd')} 
                    </div> 
                  </div> 
                ))} 
              </div> 

              {/* Habits */} 
              <AnimatePresence> 
                {habits.map((habit, index) => ( 
                  <motion.div 
                    key={habit.id} 
                    initial={{ opacity: 0, x: -20 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    transition={{ delay: index * 0.1 }} 
                    className="grid grid-cols-8 gap-2 items-center" 
                  > 
                    <div className="flex items-center space-x-2 p-2"> 
                      <div className="w-8 h-8 bg-forge-gradient rounded-lg flex items-center justify-center text-dark-950 text-sm font-bold"> 
                        {habit.icon} 
                      </div> 
                      <div className="hidden sm:block"> 
                        <div className="text-sm font-medium text-dreamforge-yellow truncate">{habit.title}</div> 
                        <div className="text-xs text-dreamforge-yellow/60">{habit.streak} days</div> 
                      </div> 
                    </div> 
                    {getWeekDays().map(day => {
                      const dayKey = `${habit.id}-${format(day, 'yyyy-MM-dd')}` 
                      const isCompleted = isHabitCompleted(habit.id, day) 
                      const isCompleting = completingHabits.has(dayKey) 
                      return ( 
                        <div key={day.toISOString()} className="flex justify-center"> 
                          <motion.button 
                            whileHover={{ scale: 1.1 }} 
                            whileTap={{ scale: 0.95 }} 
                            onClick={() => toggleHabitCompletion(habit.id, day)} 
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                              isCompleted 
                                ? 'bg-forge-gradient text-dark-950 shadow-forge glow-forge' 
                                : 'bg-dreamforge-lightblue/20 hover:bg-dreamforge-lightblue/40 text-dreamforge-yellow/60 border border-dreamforge-yellow/20'
                            } ${isCompleting ? 'habit-complete' : ''}`} 
                          > 
                            {isCompleted && ( 
                              <motion.div 
                                initial={{ scale: 0 }} 
                                animate={{ scale: 1 }} 
                                transition={{ type: "spring", stiffness: 500, damping: 30 }} 
                              > 
                                <SafeIcon icon={FiCheck} className="text-sm font-bold" /> 
                              </motion.div> 
                            )} 
                          </motion.button> 
                        </div> 
                      )
                    })} 
                  </motion.div> 
                ))} 
              </AnimatePresence> 
              
              {/* View All Habits Button for existing habits */}
              <div className="text-center pt-4 border-t border-dreamforge-yellow/20">
                <button 
                  onClick={() => navigate('/habits')}
                  className="text-dreamforge-yellow/70 hover:text-dreamforge-yellow transition-colors flex items-center justify-center space-x-2 mx-auto text-sm"
                > 
                  <SafeIcon icon={FiList} className="text-sm" /> 
                  <span>View All Habits</span>
                  <SafeIcon icon={FiArrowRight} className="text-sm" /> 
                </button>
              </div>
            </div> 
          )} 
        </div> 
      </div> 
    </div> 
  )
}

export default Dashboard